const HEX_SHORTHAND_LENGTH = 3
const HEX_FULL_LENGTH = 6

// WCAG 2 relative luminance: each channel through the sRGB curve, then weighted
// red, green, blue. https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
const SRGB = { max: 255, threshold: 0.04045, slope: 12.92, offset: 0.055, gamma: 2.4 }
const LUMINANCE_WEIGHTS = [0.2126, 0.7152, 0.0722]
// Added to both luminances of a WCAG 2 contrast ratio.
const CONTRAST_FLARE = 0.05

/**
 * Normalise a CSS hex colour to 6 digits. Vuetify hands back the SCSS value
 * verbatim, so theme colours arrive as shorthand ("#fff"), while Capacitor's
 * toolbarColor expects a full hex string.
 *
 * @returns {string|null} lowercase "#rrggbb", or null if not a hex colour.
 */
export function expandHexColor(hex) {
  const raw = typeof hex === 'string' ? hex.trim().toLowerCase() : ''
  if (!raw.startsWith('#')) return null

  const digits = raw.slice(1)
  if (!/^[0-9a-f]+$/.test(digits)) return null

  if (digits.length === HEX_FULL_LENGTH) return `#${digits}`
  if (digits.length === HEX_SHORTHAND_LENGTH) {
    return `#${digits.split('').map(d => d + d).join('')}`
  }
  return null
}

/**
 * Normalise a hex colour typed or pasted by a user. Brand guidelines quote
 * colours both ways ("#7A1F3D" and "7A1F3D"), so the leading hash is optional
 * here -- everything else is delegated to expandHexColor.
 *
 * @returns {string|null} lowercase "#rrggbb", or null if not a hex colour.
 */
export function parseHexInput(value) {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (!raw) return null

  return expandHexColor(raw.startsWith('#') ? raw : `#${raw}`)
}

const linearChannel = value => (value <= SRGB.threshold
  ? value / SRGB.slope
  : ((value + SRGB.offset) / (1 + SRGB.offset)) ** SRGB.gamma)

// Of a "#rrggbb" colour: 0 for black, 1 for white.
const relativeLuminance = hex => hex.slice(1).match(/../g)
  .map(pair => linearChannel(parseInt(pair, 16) / SRGB.max))
  .reduce((sum, channel, index) => sum + channel * LUMINANCE_WEIGHTS[index], 0)

/**
 * The WCAG 2 contrast ratio of two hex colours, in either order: from 1 (the
 * same luminance) to 21 (black on white).
 *
 * @returns {number|null} null if either is not a hex colour.
 */
export function contrastRatio(first, second) {
  const colors = [expandHexColor(first), expandHexColor(second)]
  if (colors.includes(null)) return null

  const [lighter, darker] = colors.map(relativeLuminance).sort((a, b) => b - a)
  return (lighter + CONTRAST_FLARE) / (darker + CONTRAST_FLARE)
}
